import { useState, useEffect } from "react";

// TODO: Move this to an environment variable
const API_BASE_URL = "https://clinica-server.replit.app"; // Assuming the backend runs on port 80

let authToken = null;
let loginPromise = null;

// Function to log in and get the token
const login = async () => {
  // Use hardcoded credentials for now as requested
  const email = "james@clinica.ai";
  // Using the password from the test script as a placeholder
  const password = "S0qbihrz!";

  try {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Login failed: ${response.status} ${errorData}`);
    }

    const data = await response.json();
    authToken = data.access_token;
    console.log("Successfully obtained auth token.");
    return authToken;
  } catch (error) {
    console.error("Login error:", error);
    authToken = null; // Reset token on error
    throw error; // Re-throw error to be caught by callers
  }
};

// Function to ensure login happens only once
const ensureLogin = async () => {
  if (authToken) {
    return authToken;
  }
  if (!loginPromise) {
    loginPromise = login().finally(() => {
      // Reset promise after completion (success or failure)
      // to allow retry if login failed initially.
      loginPromise = null;
    });
  }
  return loginPromise;
};

// Utility to make authenticated fetch requests
export const fetchWithAuth = async (endpoint, options = {}) => {
  try {
    const token = await ensureLogin();
    if (!token) {
      throw new Error("Authentication failed or token not available.");
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Attempt to read error message, but handle cases where it might not be JSON
      let errorBody = await response.text();
      let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
      try {
        // If the error body is JSON, parse it for more details
        const errorJson = JSON.parse(errorBody);
        errorMessage += ` - ${errorJson.error || errorBody}`;
      } catch (e) {
        // If not JSON, use the raw text
        errorMessage += ` - ${errorBody}`;
      }
      console.error(`Error fetching ${endpoint}:`, errorMessage);
      throw new Error(errorMessage);
    }

    // Handle cases with no content response (e.g., 204)
    if (response.status === 204) {
      return null;
    }

    // Assuming successful responses are JSON
    try {
      return await response.json();
    } catch (e) {
      console.error(`Error parsing JSON response from ${endpoint}:`, e);
      throw new Error(`Invalid JSON response from server for ${endpoint}.`);
    }
  } catch (error) {
    console.error(`Fetch error for ${endpoint}:`, error);
    throw error; // Re-throw error to be handled by the component
  }
};

// Custom hook for fetching data with authentication
export const useApiData = (endpoint, params = {}, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      // Construct query string
      const queryString = new URLSearchParams(params).toString();
      const fullEndpoint = queryString
        ? `${endpoint}?${queryString}`
        : endpoint;

      try {
        const result = await fetchWithAuth(fullEndpoint);
        setData(result);
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(params), ...dependencies]); // Use JSON.stringify for params comparison

  return { data, loading, error };
};

// Specific API call functions (optional but good practice)

export const getHealthSystems = () => fetchWithAuth("/health_systems");

export const getSystemProfile = (system) =>
  fetchWithAuth(`/health_system_profile?system=${encodeURIComponent(system)}`);

export const getKeyUnmetNeeds = (system) =>
  fetchWithAuth(`/key_unmet_needs?system=${encodeURIComponent(system)}`);

export const getSiteTreemap = (system, need) =>
  fetchWithAuth(
    `/site_treemap?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );

export const getHospitalDistribution = (system, need) =>
  fetchWithAuth(
    `/hospital_distribution?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );

export const getPhysicianDistribution = (system, need) =>
  fetchWithAuth(
    `/physician_distribution?system=${encodeURIComponent(system)}&need=${encodeURIComponent(need)}`,
  );
